import {
  adjustHue,
  darken,
  getLuminance,
  getScale,
  hasBadContrast,
  lighten,
  toRgba,
} from "color2k";
import { ProtonPalette, RGBArray } from "../design/types";
import { arrayToRgbString, desaturate, saturate } from "./color2k";
import { LIGHT_GRAYSCALE } from "../design/lightTheme/colors";
import { DARK_GRAYSCALE } from "../design/darkTheme/colors";

/**
 * Generates a complete color palette based on a primary color input.
 * The palette includes primary and secondary color scales with varying shades.
 *
 * @param primaryColor - The base RGB color array to generate the palette from
 * @returns A Palette object containing:
 *          - BRAND colors (primary, light primary, and secondary)
 *          - PRIMARY scale (7 shades from super dark to super light)
 *          - SECONDARY scale (7 shades from super dark to super light)
 *
 * The function:
 * 1. Creates a 12-color scale from white to the primary color to black
 * 2. Generates a complementary secondary color by shifting the hue 180 degrees
 * 3. Creates another 12-color scale for the secondary color
 * 4. Maps specific positions from these scales to create the final palette structure
 */
export const generatePalette = (baseColor: RGBArray): ProtonPalette => {
  const primaryRgba = arrayToRgbString(baseColor);

  const primaryScaleFunc = getScale("#FFFFFF", primaryRgba, "#000000");
  const primaryScale = Array.from({ length: 12 }, (_, i) => {
    const color = primaryScaleFunc(i / 11);
    return desaturate(color, 0.0085);
  });

  const adjustedPrimaryScale = primaryScale.map((color) =>
    saturate(adjustBrightnessForLuminance(color), 0.005)
  );

  const secondaryColor = toRgba(saturate(adjustHue(primaryRgba, 180), 1));

  const secondaryScaleFunc = getScale("#FFFFFF", secondaryColor, "#000000");
  const secondaryScale = Array.from({ length: 12 }, (_, i) => {
    const color = secondaryScaleFunc(i / 11);
    return desaturate(color, 0.1);
  });

  const adjustedSecondaryScale = secondaryScale.map((color) =>
    desaturate(adjustBrightnessForLuminance(color), 0.22)
  );

  const brandPrimary = darkenForContrast(
    saturate(adjustedPrimaryScale[5], 0.115)
  );
  const brandSecondary = desaturate(adjustedSecondaryScale[4], 0.02);

  return {
    BASE_COLOR: arrayToRgbString(baseColor),
    BRAND: {
      PRIMARY: brandPrimary,
      PRIMARY_LIGHT: lightenWithinRange(brandPrimary, 0.25, {
        min: 0.6,
        max: 0.8,
      }),
      PRIMARY_SUPER_LIGHT: lightenWithinRange(brandPrimary, 0.15, {
        min: 0.8,
        max: 0.9,
      }),
      SECONDARY: brandSecondary,
      SECONDARY_LIGHT: lightenWithinRange(brandSecondary, 0.25, {
        min: 0.6,
        max: 0.8,
      }),
      SECONDARY_SUPER_LIGHT: lightenWithinRange(brandSecondary, 0.15, {
        min: 0.8,
        max: 0.9,
      }),
    },
    PRIMARY: {
      //backgrounds
      SUPER_DARK: lightenWithinRange(
        lightenForContrast(primaryScale[9]),
        -0.6,
        {
          min: 0.0,
          max: 0.02,
        }
      ),
      DARK: lightenWithinRange(lightenForContrast(primaryScale[8]), -0.4, {
        min: 0.01,
        max: 0.02,
      }),
      //interactive components
      MEDIUM: lightenWithinRange(adjustedPrimaryScale[7], -0.2, {
        min: 0.0,
        max: 0.11,
      }),
      //borders and seperators
      MEDIUM_LIGHT: lightenWithinRange(
        desaturate(adjustedPrimaryScale[6], 0.1),
        0.08
      ),
      //solid colors
      LIGHT: lightenWithinRange(saturate(adjustedPrimaryScale[5], 0.1), 0.16),
      //accessible text
      LIGHTEST: lightenWithinRange(
        saturate(adjustedPrimaryScale[3], 0.2),
        0.12,
        {
          min: 0.5,
          max: 0.9,
        }
      ),
      SUPER_LIGHT: lightenWithinRange(adjustedPrimaryScale[1], 0.02, {
        min: 0.76,
        max: 0.92,
      }),
      WHITE: lightenWithinRange(adjustedPrimaryScale[1], 0.11, {
        min: 0.9,
        max: 0.98,
      }),
    },
    SECONDARY: {
      //backgrounds
      SUPER_DARK: lightenWithinRange(
        lightenForContrast(secondaryScale[9]),
        -0.6,
        {
          min: 0.0,
          max: 0.02,
        }
      ),
      DARK: lightenWithinRange(lightenForContrast(secondaryScale[8]), -0.4, {
        min: 0.01,
        max: 0.02,
      }),
      // interactive components
      MEDIUM: lightenWithinRange(adjustedSecondaryScale[7], -0.2, {
        min: 0.0,
        max: 0.11,
      }),
      //borders and seperators
      MEDIUM_LIGHT: lightenWithinRange(adjustedSecondaryScale[6], 0.1),
      //solid colors
      LIGHT: lightenWithinRange(saturate(adjustedSecondaryScale[5], 0.1), 0.16),
      //accessible text
      LIGHTEST: lightenWithinRange(
        saturate(adjustedSecondaryScale[3], 0.2),
        0.12,
        {
          min: 0.5,
          max: 0.9,
        }
      ),
      SUPER_LIGHT: lightenWithinRange(adjustedSecondaryScale[1], 0.02, {
        min: 0.76,
        max: 0.92,
      }),
      WHITE: lightenWithinRange(adjustedSecondaryScale[1], 0.11, {
        min: 0.9,
        max: 0.98,
      }),
    },
    //TODO: make theme aware grayscale color scale
    //Can we remove the basecolor after this?
    GRAYSCALE: {
      SUPER_DARK: DARK_GRAYSCALE.SUPER_DARK,
      DARK: DARK_GRAYSCALE.DARK,
      MEDIUM: DARK_GRAYSCALE.MEDIUM,
      MEDIUM_LIGHT: DARK_GRAYSCALE.MEDIUM_LIGHT,
      LIGHT: DARK_GRAYSCALE.LIGHT,
      LIGHTEST: DARK_GRAYSCALE.LIGHTEST,
      SUPER_LIGHT: DARK_GRAYSCALE.SUPER_LIGHT,
      WHITE: DARK_GRAYSCALE.WHITE,
    },
  };
};

const lightenWithinRange = (
  color: string,
  amount: number,
  range: { min: number; max: number } = { min: 0.03, max: 0.97 }
): string => {
  let adjustedColor = color;
  let currentLuminance = getLuminance(adjustedColor);

  // Validate range bounds
  if (range.min < 0) range.min = 0;
  if (range.max > 1) range.max = 1;
  if (range.min >= range.max) {
    throw new Error("Invalid range: min must be less than max");
  }

  // For lightening (positive amount)
  if (amount > 0) {
    // Calculate how much we can lighten before hitting max
    const remainingLuminance = range.max - currentLuminance;
    if (remainingLuminance <= 0) return toRgba(adjustedColor);

    // Scale the amount based on remaining room to lighten
    const scaledAmount = amount * (remainingLuminance / (1 - currentLuminance));
    adjustedColor = lighten(adjustedColor, scaledAmount);
  }
  // For darkening (negative amount)
  else if (amount < 0) {
    // Calculate how much we can darken before hitting min
    const remainingLuminance = currentLuminance - range.min;
    if (remainingLuminance <= 0) return toRgba(adjustedColor);

    // Scale the amount based on remaining room to darken
    const scaledAmount = amount * (remainingLuminance / currentLuminance);
    adjustedColor = darken(adjustedColor, Math.abs(scaledAmount));
  }

  // Verify final luminance is within bounds
  const finalLuminance = getLuminance(adjustedColor);
  if (finalLuminance > range.max) {
    adjustedColor = adjustBrightnessWithinRange(adjustedColor, range);
  } else if (finalLuminance < range.min) {
    adjustedColor = adjustBrightnessWithinRange(adjustedColor, range);
  }

  return toRgba(adjustedColor);
};

const adjustBrightnessWithinRange = (
  color: string,
  range: { min: number; max: number }
): string => {
  let adjustedColor = color;
  let luminance = getLuminance(adjustedColor);

  // Darken if too bright
  while (luminance > range.max) {
    adjustedColor = darken(adjustedColor, 0.05);
    luminance = getLuminance(adjustedColor);
  }

  // Lighten if too dark
  while (luminance < range.min) {
    adjustedColor = lighten(adjustedColor, 0.05);
    luminance = getLuminance(adjustedColor);
  }

  return toRgba(adjustedColor);
};

const lightenForContrast = (color: string): string => {
  while (hasBadContrast(color, "readable", LIGHT_GRAYSCALE.DARK)) {
    color = lighten(color, 0.01);
  }
  return toRgba(color);
};

const darkenForContrast = (color: string): string => {
  while (hasBadContrast(LIGHT_GRAYSCALE.SUPER_LIGHT, "readable", color)) {
    color = darken(color, 0.01);
  }
  return toRgba(color);
};

const adjustBrightnessForLuminance = (color: string): string => {
  let currentColor = color;
  let currentLuminance = getLuminance(toRgba(currentColor));
  let adjustmentAttempts = 0;
  const MAX_ATTEMPTS = 6;

  while (currentLuminance < 0.16 && adjustmentAttempts < MAX_ATTEMPTS) {
    adjustmentAttempts++;
    currentColor = lighten(currentColor, 0.1);
    currentLuminance = getLuminance(toRgba(currentColor));
  }

  while (currentLuminance > 0.6 && adjustmentAttempts < MAX_ATTEMPTS) {
    adjustmentAttempts++;
    currentColor = darken(currentColor, 0.1);
    currentLuminance = getLuminance(toRgba(currentColor));
  }

  return toRgba(currentColor);
};
